'use client'

import React, { useState, useCallback, useEffect } from 'react'
import { SlidersHorizontal, X, RotateCcw, Copy, Check } from 'lucide-react'
import {
  DEFAULT_NATURAL_PARAMS,
  NaturalPipelineParams,
} from '@/shared/lib/threejs/avatar/common/lipsync/natural/pipeline'
import {
  getNaturalRuntimeOverrides,
  setNaturalRuntimeOverrides,
} from '@/shared/lib/threejs/avatar/common/lipsync/natural/runtime-overrides'

/**
 * 립싱크(v18m natural_downmix) 파라미터 튜닝 패널.
 * URL 에 ?tuning=1 을 붙이면 표시된다. 값은 localStorage 오버라이드로 저장되어
 * 다음 청크(문장)부터 즉시 반영된다 — 재빌드 불필요.
 * 확정값은 "복사" 버튼으로 JSON 을 뽑아 bobbi.config.ts 의 params 에 반영한다.
 */

interface SliderDef {
  key: keyof NaturalPipelineParams
  label: string
  min: number
  max: number
  step: number
}

interface SliderSection {
  title: string
  subtitle: string
  sliders: SliderDef[]
}

const SECTIONS: SliderSection[] = [
  {
    title: '입 벌림',
    subtitle: 'jawOpen 다운믹스',
    sliders: [
      { key: 'jawExagTarget', label: '과장 목표(p95→)', min: 0, max: 1.2, step: 0.05 },
      { key: 'articOpenAmount', label: '조음 바닥값', min: 0, max: 1, step: 0.05 },
      { key: 'mouthCloseGate', label: '다뭄 게이트', min: 0, max: 1, step: 0.05 },
      { key: 'mouthDownmixOpen', label: '원순 바닥값', min: 0, max: 1, step: 0.05 },
    ],
  },
  {
    title: '원순/다뭄',
    subtitle: 'ㅗ/ㅜ · ㅁ/ㅂ/ㅍ',
    sliders: [
      { key: 'roundOpenAmount', label: '원순 시 벌림', min: 0, max: 0.5, step: 0.01 },
      { key: 'roundOUSplit', label: 'ㅗ/ㅜ 분리', min: 0, max: 1, step: 0.05 },
      { key: 'roundIsolate', label: '원순 격리', min: 0, max: 1, step: 0.05 },
      { key: 'shrugCap', label: '입다뭄 억제', min: 0, max: 1, step: 0.05 },
    ],
  },
  {
    title: '표정/스무딩',
    subtitle: 'joy · crisp · smooth',
    sliders: [
      { key: 'joyOpenAmount', label: '웃음 시 벌림', min: 0, max: 1, step: 0.05 },
      { key: 'lipSmooth', label: '립 스무딩', min: 0, max: 1, step: 0.05 },
      { key: 'crispThreshold', label: 'crisp 문턱', min: 0, max: 1, step: 0.05 },
      { key: 'crispScale', label: 'crisp 배율', min: 0.5, max: 2, step: 0.05 },
    ],
  },
  {
    title: '깜빡임',
    subtitle: 'blink',
    sliders: [
      { key: 'blinkRate', label: '분당 횟수(BPM)', min: 0, max: 20, step: 1 },
    ],
  },
]

const SLIDER_CLS = "w-full h-0.5 bg-white/20 rounded-full appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-2.5 [&::-webkit-slider-thumb]:h-2.5 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-white [&::-webkit-slider-thumb]:shadow-sm"

/** 기본값과 다른 항목만 추려 오버라이드 diff 생성 */
const diffFromDefaults = (params: NaturalPipelineParams): Partial<NaturalPipelineParams> => {
  const diff: Partial<NaturalPipelineParams> = {}
  ;(Object.keys(params) as (keyof NaturalPipelineParams)[]).forEach((key) => {
    if (params[key] !== DEFAULT_NATURAL_PARAMS[key]) diff[key] = params[key]
  })
  return diff
}

export const LipsyncTuningPanel = () => {
  const [enabled, setEnabled] = useState(false)
  const [isOpen, setIsOpen] = useState(false)
  const [copied, setCopied] = useState(false)
  const [params, setParams] = useState<NaturalPipelineParams>({ ...DEFAULT_NATURAL_PARAMS })
  const [blinkEnabled, setBlinkEnabled] = useState(true)
  const [debugLog, setDebugLog] = useState(false)

  // ?tuning=1 게이트 + 저장된 오버라이드 로드 (SSR 안전하게 클라이언트에서만)
  useEffect(() => {
    if (typeof window === 'undefined') return
    const qs = new URLSearchParams(window.location.search)
    if (qs.get('tuning') !== '1') return
    setEnabled(true)
    const saved = getNaturalRuntimeOverrides()
    if (saved?.params) setParams({ ...DEFAULT_NATURAL_PARAMS, ...saved.params })
    if (saved?.blinkEnabled !== undefined) setBlinkEnabled(saved.blinkEnabled)
    setDebugLog(window.localStorage.getItem('lipsync:debug') === '1')
  }, [])

  const persist = useCallback((nextParams: NaturalPipelineParams, nextBlink: boolean) => {
    const diff = diffFromDefaults(nextParams)
    setNaturalRuntimeOverrides({
      params: Object.keys(diff).length > 0 ? diff : undefined,
      blinkEnabled: nextBlink === true ? undefined : nextBlink, // 기본(켬)과 같으면 저장 생략
    })
  }, [])

  const handleChange = useCallback((key: keyof NaturalPipelineParams, value: number) => {
    setParams((prev) => {
      const next = { ...prev, [key]: value }
      persist(next, blinkEnabled)
      return next
    })
  }, [persist, blinkEnabled])

  const handleBlinkToggle = useCallback((on: boolean) => {
    setBlinkEnabled(on)
    persist(params, on)
  }, [persist, params])

  const handleDebugToggle = useCallback((on: boolean) => {
    setDebugLog(on)
    if (on) window.localStorage.setItem('lipsync:debug', '1')
    else window.localStorage.removeItem('lipsync:debug')
  }, [])

  const handleReset = useCallback(() => {
    setParams({ ...DEFAULT_NATURAL_PARAMS })
    setBlinkEnabled(true)
    setNaturalRuntimeOverrides(null)
  }, [])

  const copyToClipboard = useCallback(() => {
    const diff = diffFromDefaults(params)
    const output = {
      // bobbi.config.ts 의 bobbiLipsyncConfig 에 그대로 붙일 수 있는 형태
      params: diff,
      blinkEnabled,
    }
    navigator.clipboard.writeText(JSON.stringify(output, null, 2))
    setCopied(true)
    setTimeout(() => setCopied(false), 1500)
  }, [params, blinkEnabled])

  if (!enabled) return null

  return (
    <>
      {!isOpen && (
        <button
          onClick={(e) => { e.stopPropagation(); setIsOpen(true) }}
          className="absolute top-4 left-4 z-[999] p-2.5 rounded-full bg-black/40 backdrop-blur-sm text-white/80 hover:text-white hover:bg-black/60 transition-all pointer-events-auto"
          title="립싱크 튜닝"
        >
          <SlidersHorizontal size={20} />
        </button>
      )}

      {isOpen && (
        <div
          onClick={(e) => e.stopPropagation()}
          className="absolute top-4 left-4 z-[999] w-72 max-h-[90vh] overflow-y-auto bg-black/70 backdrop-blur-md rounded-2xl p-4 text-white/90 shadow-2xl scrollbar-thin pointer-events-auto"
        >
          {/* 헤더 */}
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm font-semibold tracking-wide">Lipsync Tuning</span>
            <div className="flex gap-1">
              <button onClick={copyToClipboard} className="p-1.5 rounded-lg hover:bg-white/10 transition-colors" title="확정값 JSON 복사">
                {copied ? <Check size={14} className="text-green-400" /> : <Copy size={14} />}
              </button>
              <button onClick={handleReset} className="p-1.5 rounded-lg hover:bg-white/10 transition-colors" title="기본값 복원">
                <RotateCcw size={14} />
              </button>
              <button onClick={() => setIsOpen(false)} className="p-1.5 rounded-lg hover:bg-white/10 transition-colors">
                <X size={14} />
              </button>
            </div>
          </div>

          {SECTIONS.map((section) => (
            <div key={section.title} className="mb-3">
              <div className="flex items-baseline gap-1.5 mb-1.5">
                <span className="text-[11px] font-bold text-blue-400 tracking-wider">{section.title}</span>
                <span className="text-[10px] text-white/40">{section.subtitle}</span>
              </div>
              {section.sliders.map((s) => {
                const value = params[s.key]
                const isChanged = value !== DEFAULT_NATURAL_PARAMS[s.key]
                return (
                  <div key={s.key} className="mb-1.5">
                    <div className="flex justify-between text-[10px] mb-0.5">
                      <span className={isChanged ? 'text-yellow-300' : 'text-white/50'}>{s.label}</span>
                      <span className="text-white/30 tabular-nums">{value.toFixed(2)}</span>
                    </div>
                    <input
                      type="range"
                      min={s.min} max={s.max} step={s.step} value={value}
                      onChange={(e) => handleChange(s.key, parseFloat(e.target.value))}
                      className={SLIDER_CLS}
                    />
                  </div>
                )
              })}
            </div>
          ))}

          {/* 토글 */}
          <div className="border-t border-white/5 pt-2.5 flex flex-col gap-1.5">
            <label className="flex items-center justify-between text-[11px] cursor-pointer">
              <span className="text-white/60">자동 깜빡임</span>
              <input type="checkbox" checked={blinkEnabled} onChange={(e) => handleBlinkToggle(e.target.checked)} className="accent-blue-400" />
            </label>
            <label className="flex items-center justify-between text-[11px] cursor-pointer">
              <span className="text-white/60">콘솔 디버그 로그</span>
              <input type="checkbox" checked={debugLog} onChange={(e) => handleDebugToggle(e.target.checked)} className="accent-blue-400" />
            </label>
          </div>

          <div className="mt-3 pt-2 border-t border-white/10 text-[10px] text-white/30 text-center">
            변경값은 다음 문장부터 적용 · 노란 라벨 = 기본값과 다름
          </div>
        </div>
      )}
    </>
  )
}
